/**
 * Attachment Grid Viewer Component
 * ===============================
 * 
 * Displays uploaded attachments in a grid layout with separate sections for media
 * (images/videos) and files. This component is rendered within the attachment upload
 * section of individual forms and provides a visual overview of all attachments.
 * 
 * Core Features:
 * - Grid display of media attachments with thumbnails
 * - List display of file attachments with icons
 * - Click-to-view functionality using the attachment viewer modal
 * - Delete individual attachments or all attachments
 * - File type detection and appropriate icon display
 * - Filename truncation and file size formatting
 * - Minimum attachment requirements display
 * 
 * Media Handling:
 * - Images: Display actual image thumbnails
 * - Videos: Show video thumbnails with play button overlay
 * - Files: Display file type icons with metadata
 * 
 * User Interaction:
 * - Click media/file to open in full-screen viewer modal
 * - Delete buttons for individual attachments (when editable)
 * - Bulk delete all attachments option
 * - Responsive grid layout for media items
 * 
 * @author MacSafety Development Team
 * @version 1.0
 */

import { Component, Input, Output, EventEmitter } from '@angular/core';
import { AttachmentUploadData } from '../../../../../models/forms/attachments/attachment-upload';
import { AttachmentUploadService } from '../../../../../services/attachment-upload.service';
import { getUserFriendlyFileType, getFileTypeDisplayName, getFileTypeIonicIcon } from '../../../../../utils/file-type';

@Component({
  selector: 'app-attachment-grid-viewer',
  templateUrl: './attachment-grid-viewer.component.html',
  styleUrls: ['./attachment-grid-viewer.component.css']
})
export class AttachmentGridViewerComponent {
  @Input() attachments: AttachmentUploadData[] = [];
  @Input() editable: boolean = true;
  @Input() minAttachments: number = 0;
  @Input() identifier: string = '';
  @Output() deleteAttachment = new EventEmitter<string>();
  @Output() deleteAllAttachments = new EventEmitter<void>();

  public isAttachmentModalOpen: boolean = false;
  public selectedAttachmentIndex: number = 0;

  constructor(private attachmentService: AttachmentUploadService) {}

  /**
   * Deletes a specific attachment using the attachment service.
   * Emits the deleteAttachment event for parent component notification.
   * 
   * @param attachmentId - The unique identifier of the attachment to delete
   */
  onDeleteAttachment(attachmentId: string): void {
    this.attachmentService.deleteAttachment(this.identifier, attachmentId);
  }

  /**
   * Deletes all attachments for the current form identifier.
   * Emits the deleteAllAttachments event for parent component notification.
   */
  onDeleteAllAttachments(): void {
    this.attachmentService.deleteAllAttachments(this.identifier);
  }

  /**
   * Opens the attachment viewer modal for a specific attachment.
   * Sets the selected attachment index and opens the modal.
   * 
   * @param index - The index of the attachment to view
   */
  onOpenAttachmentModal(index: number): void {
    this.selectedAttachmentIndex = index;
    this.isAttachmentModalOpen = true;
  }

  /**
   * Closes the attachment viewer modal.
   * Resets the modal state to closed.
   */
  closeAttachmentModal(): void {
    this.isAttachmentModalOpen = false;
  }

  /**
   * Determines if the attachment is media (image or video) that should show thumbnail.
   * Used to categorize attachments for display purposes.
   * 
   * @param attachment - The attachment to check
   * @returns True if the attachment is an image or video, false otherwise
   */
  isMedia(attachment: AttachmentUploadData): boolean {
    return attachment.type.startsWith('image/') || attachment.type.startsWith('video/');
  }

  /**
   * Determines if the attachment is a video file.
   * Used to apply video-specific styling and overlay elements.
   * 
   * @param attachment - The attachment to check
   * @returns True if the attachment is a video, false otherwise
   */
  isVideo(attachment: AttachmentUploadData): boolean {
    return attachment.type.startsWith('video/');
  }

  /**
   * Gets the appropriate Ionic icon for the file type using utility functions.
   * Provides visual representation for different file types in the file list.
   * 
   * @param attachment - The attachment to get the icon for
   * @returns String representing the Ionic icon name
   */
  getFileIcon(attachment: AttachmentUploadData): string {
    return getFileTypeIonicIcon(attachment.type, attachment.name);
  }

  /**
   * Gets the display name for the file type using utility functions.
   * Provides human-readable file type descriptions.
   * 
   * @param attachment - The attachment to get the display name for
   * @returns String representing the file type display name
   */
  getFileTypeDisplayName(attachment: AttachmentUploadData): string {
    return getFileTypeDisplayName(attachment.type, attachment.name);
  }

  /**
   * Gets the full file type information using utility functions.
   * Provides comprehensive file type details for display purposes.
   * 
   * @param attachment - The attachment to get the file type info for
   * @returns Object containing file type information
   */
  getFileTypeInfo(attachment: AttachmentUploadData) {
    return getUserFriendlyFileType(attachment.type, attachment.name);
  }

  /**
   * Formats file size from bytes to human-readable format.
   * Converts bytes to appropriate units (B, KB, MB, GB) with one decimal place.
   * 
   * @param bytes - The file size in bytes
   * @returns Formatted file size string
   */
  formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
  }

  /**
   * Truncates filename for display purposes to maintain consistent layout.
   * Preserves file extensions while truncating the main filename portion.
   * 
   * @param filename - The filename to truncate
   * @param maxLength - Maximum length for the truncated filename (default: 25)
   * @returns Truncated filename with ellipsis
   */
  truncateFilename(filename: string, maxLength: number = 25): string {
    if (filename.length <= maxLength) return filename;
    
    const lastDotIndex = filename.lastIndexOf('.');
    if (lastDotIndex === -1) {
      return filename.substring(0, maxLength - 3) + '...';
    }
    
    const name = filename.substring(0, lastDotIndex);
    const extension = filename.substring(lastDotIndex);
    const availableSpace = maxLength - extension.length - 3;
    
    if (availableSpace <= 0) return '...' + extension;
    
    return name.substring(0, availableSpace) + '...' + extension;
  }

  /**
   * Gets all file attachments (non-media) for display in the files section.
   * Filters out images and videos to show only document and other file types.
   * 
   * @returns Array of non-media attachments
   */
  getFileAttachments(): AttachmentUploadData[] {
    return this.attachments.filter(attachment => !this.isMedia(attachment));
  }

  /**
   * Gets all media attachments (images and videos) for display in the media grid.
   * Filters to show only image and video files for thumbnail display.
   * 
   * @returns Array of media attachments
   */
  getMediaAttachments(): AttachmentUploadData[] {
    return this.attachments.filter(attachment => this.isMedia(attachment));
  }

  /**
   * Gets the index of an attachment in the original attachments array.
   * Used to maintain proper indexing when opening the attachment viewer modal.
   * 
   * @param attachment - The attachment to find the index for
   * @returns The index of the attachment in the original array, or -1 if not found
   */
  getAttachmentIndex(attachment: AttachmentUploadData): number {
    return this.attachments.findIndex(att => att.id === attachment.id);
  }
}